import { MouseEvent, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ButtonProps, TooltipProps } from 'src/components/atoms';
import { useOutsideClick } from 'src/hooks';
import {
  ButtonAndTooltipContainer,
  StyledButton,
  StyledTooltip,
} from './ButtonAndTooltip.styles';

interface Props {
  disabled?: boolean;
  tooltipProps: Omit<TooltipProps, 'root'>;
  buttonProps: ButtonProps;
  shouldUsePortal?: boolean;
  'data-testid'?: string;
}

export const ButtonAndTooltip = ({
  disabled = false,
  shouldUsePortal,
  buttonProps,
  tooltipProps,
}: Props) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [root, setRoot] = useState<HTMLButtonElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  useOutsideClick(tooltipRef, () => {
    if (tooltipVisible) {
      setTooltipVisible(false);
    }
  });

  const showTooltipWithPortal = shouldUsePortal && tooltipVisible && root;
  const showTooltipWithoutPortal = !shouldUsePortal && tooltipVisible && root;

  return (
    <ButtonAndTooltipContainer ref={tooltipRef}>
      <StyledButton
        {...buttonProps}
        setRoot={setRoot}
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          setTooltipVisible(!tooltipVisible);
          buttonProps.onClick?.(e);
        }}
        clicked={tooltipVisible}
        disabled={disabled}
        noTextWrap
      />
      {showTooltipWithPortal &&
        createPortal(
          <StyledTooltip
            {...tooltipProps}
            root={root}
            extraPaddingForRoot={3}
          />,
          document.body,
        )}
      {showTooltipWithoutPortal && (
        <StyledTooltip
          {...tooltipProps}
          root={root}
          extraPaddingForRoot={tooltipProps.extraPaddingForRoot ?? 3}
        />
      )}
    </ButtonAndTooltipContainer>
  );
};
